import dayjs from 'dayjs';

import { User } from '~hooks/useUsers/models';

declare global {
  interface Window {
    Intercom?: (command: string, ...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
  }
}

/**
 * Opens the Intercom messenger with a pre-filled message. Intercom is only
 * loaded in production (see `public/intercom.js`), so this is a no-op in other
 * environments where `window.Intercom` is undefined.
 */
export const openIntercomMessage = (message: string) => {
  window.Intercom?.('showNewMessage', message);
};

/**
 * Pushes the current user and their company into Intercom so support and success
 * conversations show who is on the other end instead of a bare email address.
 *
 * Intercom only associates a contact with a company when the company arrives as a nested
 * `company` object. We previously sent top-level `company_id`/`company_name`, which are
 * not supported attributes and are discarded silently — hence contacts reaching the
 * support inbox with no company.
 * https://developers.intercom.com/installing-intercom/web/attributes-objects/
 *
 * Everything here is a native Intercom attribute, so this needs no workspace setup.
 * Custom attributes are the exception: Intercom accepts unrecognised ones and drops their
 * values without erroring, so any new key must first exist as a data attribute. `Roles`
 * already does, because the driver app writes it.
 *
 * Writes `window.intercomSettings` for a messenger that has not booted yet and calls
 * `update` for one that already has: the widget script is injected asynchronously (see
 * `src/main.tsx`), so either can win the race against the current-user request.
 *
 * `public/intercom.js` is only injected in production, so the absence of
 * `window.intercomSettings` doubles as the "Intercom is not installed here" check and
 * makes this a no-op in every other environment.
 */
export const syncIntercomUser = (user: User) => {
  const intercomIsInstalled = 'intercomSettings' in window;

  if (!intercomIsInstalled) {
    return;
  }

  const settings = {
    ...window.intercomSettings,
    user_id: user.id,
    user_hash: user.intercomHash,
    name: user.fullName,
    ...(user.email ? { email: user.email } : {}),
    ...(user.phone ? { phone: user.phone } : {}),
    // Intercom expects signup dates as unix seconds, not milliseconds or an ISO string.
    // Plain dayjs() rather than dayjs.tz() on purpose: unix() wants the absolute instant,
    // and dayjs.tz() would re-read the ISO string as wall-clock time in the default
    // timezone and shift the epoch by that offset.
    ...(user.createdAt ? { created_at: dayjs(user.createdAt).unix() } : {}),
    ...(user.company
      ? { company: { company_id: user.company.id, name: user.company.legalName } }
      : {}),
    // Capitalised to match the key the driver app already writes (horizon-mobile
    // account_scaffold.dart) so both clients feed a single Intercom data attribute.
    // Guard on length, not truthiness: User.parse defaults to [], and sending the
    // resulting empty string would clear whatever the driver app last wrote.
    ...(user.userRoles?.length
      ? {
          custom_attributes: {
            Roles: user.userRoles.map((role) => role.name).join(', '),
          },
        }
      : {}),
  };

  window.intercomSettings = settings;
  window.Intercom?.('update', settings);
};
